/**
 * SettingsPage Component
 * Teacher profile settings for viewing and managing feedback style.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTeacherProfile } from '../hooks/useTeacherProfile';
import { useToast } from '../components/Toast';
import FeedbackStyleSection from '../components/Settings/FeedbackStyleSection';
import InitializeProfileWizard from '../components/Settings/InitializeProfileWizard';

const SettingsPage: React.FC = () => {
  const {
    profile,
    status,
    loading,
    error,
    updateProfile,
    addExample,
    removeExample,
    reExtract,
    initialize,
    clearError,
  } = useTeacherProfile();

  const { showToast } = useToast();

  // Handle initialization
  const handleInitialize = async (examples: string[]) => {
    try {
      await initialize(examples);
      showToast('success', 'Profile initialized successfully');
    } catch {
      showToast('error', 'Failed to initialize profile');
    }
  };

  // Handle profile updates with toast
  const handleUpdateProfile = async (updates: Parameters<typeof updateProfile>[0]) => {
    try {
      await updateProfile(updates);
      showToast('success', 'Profile updated');
    } catch {
      showToast('error', 'Failed to update profile');
    }
  };

  // Handle adding example
  const handleAddExample = async (text: string, category: string) => {
    try {
      await addExample(text, category);
      showToast('success', 'Example added');
    } catch {
      showToast('error', 'Failed to add example');
    }
  };

  // Handle removing example
  const handleRemoveExample = async (index: number) => {
    try {
      await removeExample(index);
      showToast('success', 'Example removed');
    } catch {
      showToast('error', 'Failed to remove example');
    }
  };

  // Handle re-extraction
  const handleReExtract = async () => {
    try {
      await reExtract();
      showToast('success', 'Style re-analyzed successfully');
    } catch {
      showToast('error', 'Failed to re-analyze style');
    }
  };

  // Show loading state
  if (loading && !profile && !status) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center space-x-4">
            <Link
              to="/grade"
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back to Grading
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
          </div>
        </header>
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-gray-500">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading profile...
          </div>
        </div>
      </div>
    );
  }

  // Show initialization wizard if no profile
  if (!profile && status && !status.has_profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center space-x-4">
            <Link
              to="/grade"
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back to Grading
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
          </div>
        </header>
        <div className="max-w-2xl mx-auto py-8 px-4">
          <InitializeProfileWizard
            onInitialize={handleInitialize}
            isLoading={loading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link
              to="/grade"
              className="text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Back to Grading
            </Link>
            <h1 className="text-xl font-semibold text-gray-900">Settings</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-3xl mx-auto py-8 px-4">
        {/* Error banner */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Profile content */}
        {profile && (
          <FeedbackStyleSection
            profile={profile}
            onUpdateProfile={handleUpdateProfile}
            onAddExample={handleAddExample}
            onRemoveExample={handleRemoveExample}
            onReExtract={handleReExtract}
            isLoading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
